// Вкладка «Совет»: состав, отношения, карточки участников. Вынесено
// из CampaignSetupPage.tsx без изменения поведения (Фаза 1).

import { type CampaignSetup, type CampaignRelationshipOverview, type CharacterPublic, type SetupMember } from "../api";
import { InvitePlayersPanel } from "./InvitePlayersPanel";
import { BlockHeader, Icon, MemberAvatar, type CouncilSection } from "./shared";

function CouncilTab({
  campaignId,
  setup,
  currentMember,
  action,
  section,
}: {
  campaignId: string;
  setup: CampaignSetup;
  currentMember?: SetupMember;
  action: (label: string, run: () => Promise<unknown>) => Promise<boolean>;
  section: CouncilSection;
}) {
  const characterByMember = new Map(
    setup.characters.map((character) => [
      character.campaignMemberId,
      character,
    ]),
  );
  if (section === "relationships")
    return <RelationshipOverviewPanel relationships={setup.relationships} />;
  return (
    <div className="cc-tab-stack">
      {setup.canManage && (
        <InvitePlayersPanel
          campaignId={campaignId}
          setup={setup}
          action={action}
        />
      )}
      <section className="cc-block">
        <BlockHeader
          eyebrow="Публичный состав"
          title="Временный совет"
          icon="users"
        />
        <p className="cc-explainer">
          Здесь видны публичные должности, обязанности и особые полномочия всех
          участников кампании.
        </p>
        <div className="cc-council-grid">
          {setup.members.map((member) => (
            <CouncilMemberCard
              key={member.campaignMemberId}
              campaignId={campaignId}
              member={member}
              character={characterByMember.get(member.campaignMemberId)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
function RelationshipOverviewPanel({
  relationships,
}: {
  relationships: CampaignRelationshipOverview;
}) {
  const pressure = relationships.pressure;
  return (
    <section className="cc-block cc-relationship-panel">
      <BlockHeader
        eyebrow="Доверие, нагрузка и модификаторы"
        title="Политическая среда"
        icon="faction"
      />
      <p className="cc-explainer">
        Политическая среда — это механический слой доверия и давления. Отношение
        фракций влияет на переговоры, поддержку и сопротивление инициативам.
        Нагрузка институтов показывает, насколько службы изношены: перегруженная
        служба даёт штрафы к действиям и повышает риск решений, где она
        выступает исполнителем.
      </p>
      <article
        className={`cc-relationship-council pressure-${pressure.level.toLowerCase()}`}
      >
        <div>
          <strong>{relationships.council.label}</strong>
          <span>{pressure.label}</span>
        </div>
        <p>{relationships.council.summary}</p>
        <p>{pressure.summary}</p>
        <div>
          <span>
            Среднее отношение фракций:{" "}
            {relationships.council.averageFactionAttitude}
          </span>
          <span>
            Средняя нагрузка институтов:{" "}
            {relationships.council.averageInstitutionLoad}
          </span>
          <span>Давление: {pressure.pressureScore}</span>
        </div>
        <small>{pressure.modifierHint}</small>
      </article>
      {pressure.factors.length > 0 && (
        <section className="cc-relationship-pressure-list">
          <h3>Главные источники давления</h3>
          <div>
            {pressure.factors.map((factor) => (
              <article key={`${factor.kind}-${factor.code}`}>
                <header>
                  <strong>{factor.displayName}</strong>
                  <span>
                    {factor.label} · +{factor.value}
                  </span>
                </header>
                <p>{factor.explanation}</p>
              </article>
            ))}
          </div>
        </section>
      )}
      {(relationships.factions.length > 0 ||
        relationships.institutions.length > 0) && (
        <div className="cc-relationship-grid full">
          {relationships.factions.map((faction) => (
            <article
              className={`cc-relationship-card posture-${faction.posture.toLowerCase()}`}
              key={`faction-${faction.code}`}
            >
              <header>
                <strong>{faction.displayName}</strong>
                <span>{faction.postureLabel}</span>
              </header>
              <p>{faction.summary}</p>
              <small>
                Отношение {faction.attitude} · влияние {faction.influence}
              </small>
              <em>{faction.mechanicalHint}</em>
            </article>
          ))}
          {relationships.institutions.map((institution) => (
            <article
              className={`cc-relationship-card strain-${institution.strain.toLowerCase()}`}
              key={`institution-${institution.code}`}
            >
              <header>
                <strong>{institution.displayName}</strong>
                <span>{institution.strainLabel}</span>
              </header>
              <p>{institution.summary}</p>
              <small>
                Уровень {institution.level} · нагрузка {institution.load}
              </small>
              <em>{institution.mechanicalHint}</em>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
function CouncilMemberCard({
  campaignId,
  member,
  character,
}: {
  campaignId: string;
  member: SetupMember;
  character?: CharacterPublic;
}) {
  return (
    <article
      className={`cc-council-card${member.isCurrentUser ? " current" : ""}`}
    >
      <div className="cc-member-head">
        <MemberAvatar campaignId={campaignId} member={member} large />
        <div>
          <span>
            {member.displayName}
            {member.isCurrentUser ? " · вы" : ""}
          </span>
          <h3>
            {character?.publicTitle ??
              (member.memberRole === "OWNER"
                ? "Организатор кампании"
                : "Участник")}
          </h3>
        </div>
      </div>
      {character ? (
        <>
          <p>{character.publicDuties}</p>
          <div className="cc-power">
            <span>
              <Icon name="power" />
              Особое полномочие
            </span>
            {character.specialPower}
          </div>
        </>
      ) : (
        <p className="cc-muted">
          Должность будет назначена после создания мира.
        </p>
      )}
    </article>
  );
}

export {
  CouncilTab,
  RelationshipOverviewPanel,
  CouncilMemberCard,
};
